import type {
  CommonResponse,
  CommonResponseCollectionWorkbookPageVo,
  CommonResponseSliceWorkbookSearchResponse,
  CoverData as CoverDataWire,
  CreateCustomSignatureWorkbookFilterParams,
  CustomSignatureWorkbookCreationRequestV3,
  CustomSignatureWorkbookCreationResponse,
  CustomSignatureWorkbookFilterCreationResponse,
  CustomSignatureWorkbookFilterResponseV3,
  CustomSignatureWorkbookUpdateRequestV3,
  DownloadData,
  DownloadParams,
  GetWorkbookAssignByWorkbookIdData,
  GetWorkbookByWorkbookIdData,
  GetWorkbookPagesById1Params,
  GetWorkbooksByParams,
  Pageable,
  WorkbookAssignSearchRequest,
  WorkbookPageResponse,
  WorkbookPageVo,
} from '~/api-spec/__generated__/main'
import { httpClient, type RequestConfig, type RequestMeta } from '~/http/client'
import type { WithNullableOptionals } from '~/http/request'
import type { Slice } from '~/schema/slice'

import {
  adaptCreatedWorkbookId,
  adaptSignatureWorkbookDraft,
  adaptSignatureWorkbookDraftId,
  adaptWorkbookAssignedStudents,
  adaptWorkbookDetail,
  adaptWorkbookFile,
  adaptWorkbookList,
  adaptWorkbookPageImages,
  adaptWorkbookPagesWithProblems,
} from './adapters'
import type {
  CustomSignatureWorkbook,
  SignatureWorkbookDraft,
  Workbook,
  WorkbookAssignedStudent,
  WorkbookDetail,
  WorkbookFile,
  WorkbookListItem,
  WorkbookPageImage,
  WorkbookPageWithProblems,
} from './schemas'

// 교재 목록의 배정 조건(withAssignRequest)은 스펙이 객체 하나로 그리지만 서버는 평탄한 쿼리로 받는다
// (@ModelAttribute). assignSearchType 은 서버가 withAssigned 로 기본값을 정하므로 선택이다.
// 2026-10-08 백엔드 develop 의 workbook/controller/WorkbookController.kt·request/WorkbookAssignSearchRequest.kt 로 확인.
export type WorkbookListParams = Omit<GetWorkbooksByParams, 'pageable' | 'withAssignRequest'> &
  Partial<WorkbookAssignSearchRequest> &
  Pageable
// 표지 데이터의 nullable 칸(subtitle·로고 등)을 스펙이 optional 로만 그린다. 2026-10-08 백엔드 develop 의
// workbook/model/dto/CoverData.kt 로 확인.
export type CustomSignatureWorkbookBody = Omit<
  CustomSignatureWorkbookCreationRequestV3 & CustomSignatureWorkbookUpdateRequestV3,
  'coverData'
> & { coverData: WithNullableOptionals<CoverDataWire> }
// 표지 편집 1단계는 로고 파일을 함께 올려 multipart 로 보낸다. 텍스트 필드는 스펙이 쿼리로 그리지만 서버는
// 같은 폼의 필드로 받는다.
export type SignatureWorkbookDraftBody = CreateCustomSignatureWorkbookFilterParams

/***************** 교재 *****************/

export const getWorkbooks = async (
  params: WorkbookListParams,
  config?: Pick<RequestConfig, 'meta' | 'signal'>,
): Promise<Slice<WorkbookListItem>> => {
  const body = await httpClient().get<CommonResponseSliceWorkbookSearchResponse>('/workbook', {
    params,
    ...config,
  })
  return adaptWorkbookList(body.data)
}

/** 원본 교재의 쪽마다 실린 문제(교재를 보며 문제 고르기). 이 컨트롤러는 봉투 없이 본문을 바로 낸다 */
export const getWorkbookPagesWithProblems = async (
  workbookId: Workbook['id'],
  meta?: RequestMeta,
): Promise<WorkbookPageWithProblems[]> =>
  adaptWorkbookPagesWithProblems(
    await httpClient().get<WorkbookPageResponse[]>(`/workbook/${workbookId}/page/v2`, { meta }),
  )

/** 내 교재를 지운다(여러 권) */
export const deleteCustomWorkbooks = async (
  workbookIds: Workbook['id'][],
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().delete<CommonResponse>('/workbook', { data: workbookIds, meta })
}

/***************** 교재 원본 쪽 이미지 *****************/

// workbookType 을 빼면 서버가 CUSTOM_SIGNATURE 로 본다. 원본 시그니처 교재의 쪽을 함께 보려면 ALL 을 넘긴다
export type CustomSignatureWorkbookPageImageParams = Omit<GetWorkbookPagesById1Params, 'id'>

export const getCustomSignatureWorkbookPageImages = async (
  workbookId: Workbook['id'],
  params?: CustomSignatureWorkbookPageImageParams,
  meta?: RequestMeta,
): Promise<WorkbookPageImage[]> => {
  const body = await httpClient().get<CommonResponseCollectionWorkbookPageVo>(
    `/workbook/custom-signature/${workbookId}/page`,
    { params, meta },
  )
  return adaptWorkbookPageImages(body.data) ?? []
}

/** 원본 시그니처 교재의 쪽 이미지(교재 미리보기) */
export const getSignatureWorkbookPageImages = async (
  workbookId: Workbook['id'],
  meta?: RequestMeta,
): Promise<WorkbookPageImage[]> => {
  const body = await httpClient().get<CommonResponseCollectionWorkbookPageVo>(
    `/workbook/signature/${workbookId}/page`,
    { meta },
  )
  return adaptWorkbookPageImages(body.data) ?? []
}

/** 학원그룹 교재 컨트롤러는 봉투 없이 본문을 바로 낸다 */
export const getAcademyGroupWorkbookPageImages = async (
  workbookId: Workbook['id'],
  meta?: RequestMeta,
): Promise<WorkbookPageImage[]> =>
  adaptWorkbookPageImages(
    await httpClient().get<WorkbookPageVo[]>(`/workbook/academy-group/${workbookId}/page`, {
      meta,
    }),
  )

/***************** 커스텀 시그니처 교재 *****************/
// V3 컨트롤러는 봉투 없이 본문을 바로 낸다. V2(삭제·복원)는 CommonResponse 봉투다.

export const createSignatureWorkbookDraft = async (
  body: FormData,
  meta?: RequestMeta,
): Promise<SignatureWorkbookDraft['filterId']> =>
  adaptSignatureWorkbookDraftId(
    await httpClient().post<CustomSignatureWorkbookFilterCreationResponse>(
      '/v3/workbook/custom-signature/filter',
      body,
      { meta },
    ),
  )

export const getSignatureWorkbookDraft = async (
  filterId: SignatureWorkbookDraft['filterId'],
  meta?: RequestMeta,
): Promise<SignatureWorkbookDraft> =>
  adaptSignatureWorkbookDraft(
    await httpClient().get<CustomSignatureWorkbookFilterResponseV3>(
      '/v3/workbook/custom-signature/filter',
      { params: { filterId }, meta },
    ),
  )

export const createCustomSignatureWorkbook = async (
  body: CustomSignatureWorkbookBody,
  meta?: RequestMeta,
): Promise<CustomSignatureWorkbook['id']> =>
  adaptCreatedWorkbookId(
    await httpClient().post<CustomSignatureWorkbookCreationResponse>(
      '/v3/workbook/custom-signature',
      body,
      { meta },
    ),
  )

export const updateCustomSignatureWorkbook = async (
  workbookId: CustomSignatureWorkbook['id'],
  body: CustomSignatureWorkbookBody,
  meta?: RequestMeta,
): Promise<CustomSignatureWorkbook['id']> =>
  adaptCreatedWorkbookId(
    await httpClient().put<CustomSignatureWorkbookCreationResponse>(
      `/v3/workbook/custom-signature/${workbookId}`,
      body,
      { meta },
    ),
  )

export const deleteCustomSignatureWorkbook = async (
  workbookId: CustomSignatureWorkbook['id'],
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().delete<CommonResponse>(`/v2/workbook/custom-signature/${workbookId}`, { meta })
}

export const restoreCustomSignatureWorkbook = async (
  workbookId: CustomSignatureWorkbook['id'],
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post<CommonResponse>(
    `/v2/workbook/custom-signature/${workbookId}/restore`,
    undefined,
    { meta },
  )
}

/***************** 교재 상세와 파일(선생님 앱) *****************/

export const getWorkbookDetail = async (
  workbookId: Workbook['id'],
  meta?: RequestMeta,
): Promise<WorkbookDetail> => {
  const body = await httpClient().get<GetWorkbookByWorkbookIdData>(`/workbook/${workbookId}`, {
    meta,
  })
  return adaptWorkbookDetail(body.data)
}

/** 이 교재를 받은 학생들 */
export const getWorkbookAssignedStudents = async (
  workbookId: Workbook['id'],
  meta?: RequestMeta,
): Promise<WorkbookAssignedStudent[]> => {
  const body = await httpClient().get<GetWorkbookAssignByWorkbookIdData>(
    `/workbook/${workbookId}/assign`,
    { meta },
  )
  return adaptWorkbookAssignedStudents(body.data) ?? []
}

// version 은 요청 함수가 v2 로 고정한다
export type WorkbookFileParams = Omit<DownloadParams, 'version'>
export type WorkbookPdfType = WorkbookFileParams['workbookPdfTypes'][number]

/** 교재 PDF 주소. 이 엔드포인트는 봉투 없이 본문을 바로 낸다 */
export const getWorkbookFile = async (
  params: WorkbookFileParams,
  meta?: RequestMeta,
): Promise<WorkbookFile> =>
  adaptWorkbookFile(
    await httpClient().get<DownloadData>('/workbook/v2/download', {
      params: { ...params, version: 'v2' },
      meta,
    }),
  )
